import { useMemo, useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for managing drawer content state and behavior
 * @param {Object} props - The props object
 * @param {Object} props.flags - Feature flags
 * @param {Array} props.navOptionsArray - Array of navigation options
 * @returns {Object} - Drawer content state and handlers
 */
export const useDrawerContent = ({ flags, navOptionsArray }) => {
  const initialOpenState = useMemo(
    () =>
      navOptionsArray?.reduce((acc, option) => {
        acc[option.value] = false;
        return acc;
      }, {}),
    [navOptionsArray],
  );
  const [open, setOpen] = useState(initialOpenState);

  // Only initialize these states if the UX improvements feature flag is enabled
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [previousHeight, setPreviousHeight] = useState(0);
  const [activeMenu, setActiveMenu] = useState(null);
  const [isNavigatingDown, setIsNavigatingDown] = useState(true);

  // Reset states when navOptionsArray changes
  const [resetHeight, setResetHeight] = useState(false);
  useEffect(() => {
    if (flags.navigationMenuUxImprovements) {
      // Reset previousHeight to 0
      setPreviousHeight(0);
      // Set resetHeight to true to trigger height reset in NavListItem
      setResetHeight(true);
      // Reset resetHeight after a short delay to avoid continuous resets
      const timer = setTimeout(() => {
        setResetHeight(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [navOptionsArray, initialOpenState, flags.navigationMenuUxImprovements]);

  // Custom setOpen function that also tracks the active menu and navigation direction
  const handleSetOpen = useCallback(
    (newOpenState) => {
      // If the UX improvements feature flag is not enabled, just update the open state
      if (!flags.navigationMenuUxImprovements) {
        setOpen(newOpenState);
        return;
      }

      // Find which menu is being opened
      const openingMenu = Object.keys(newOpenState).find(
        (key) => newOpenState[key] && !open[key],
      );

      if (openingMenu) {
        // Determine navigation direction based on menu position in the array
        const menuIndex = navOptionsArray.findIndex(
          (option) => option.value === openingMenu,
        );
        const activeMenuIndex = activeMenu
          ? navOptionsArray.findIndex((option) => option.value === activeMenu)
          : -1;

        // If activeMenuIndex is -1 (no previous menu) or menuIndex > activeMenuIndex,
        // we're navigating down, otherwise we're navigating up
        setIsNavigatingDown(
          activeMenuIndex === -1 || menuIndex > activeMenuIndex,
        );
        setActiveMenu(openingMenu);
      }

      setOpen(newOpenState);
    },
    [flags.navigationMenuUxImprovements, navOptionsArray, open, activeMenu],
  );

  return {
    initialOpenState,
    open,
    isTransitioning,
    setIsTransitioning,
    previousHeight,
    setPreviousHeight,
    activeMenu,
    isNavigatingDown,
    resetHeight,
    handleSetOpen,
  };
};

export default useDrawerContent;
